'use client'

import { clsx } from 'clsx'
import Script from 'next/script'
import { type FC, type MouseEvent as ReactMouseEvent, useCallback } from 'react'
import useSWRImmutable from 'swr/immutable'

import { EXPERIMENTS_TRACK_EVENT_REMOVE_BANNERS_PDP_APP_INSTALL } from '../experiments/eventTracking'
import { useExperimentRemoveBannersTracking } from '../experiments/experiment-remove-banners/useExperimentRemoveBannersTracking'
import { useGetExperiment } from '../experiments/hooks'
import { EXPERIMENT_NAME_REMOVE_BANNERS } from '../experiments/model/EXPERIMENT_NAME'
import { EXPERIMENT_VARIATION } from '../experiments/model/EXPERIMENT_VARIATION'
import { gtmDataLayerWaitTillEventLoaded } from '../gtm'

import { type AppsFlyerBannerConfig } from './appsFlyerBannerGetConfig'

import styles from './AppsFlyerBanner.module.css'

const APPS_FLYER_CONTAINER_ID = 'apps-flyer-container'

const eventFetcher = async (): ReturnType<
  typeof gtmDataLayerWaitTillEventLoaded
> =>
  gtmDataLayerWaitTillEventLoaded({
    eventName: 'gtm.data.loaded',
    timeout: 2000,
  })

type AppsFlyerBannerProps = { config: AppsFlyerBannerConfig }

export const AppsFlyerBanner: FC<AppsFlyerBannerProps> = ({ config }) => {
  const { key } = config

  const { isEnabled, variant = EXPERIMENT_VARIATION.DEFAULT } =
    useGetExperiment(EXPERIMENT_NAME_REMOVE_BANNERS)

  const { data } = useSWRImmutable('gtm.data.loaded', eventFetcher)

  const experimentRemoveBannersTracking = useExperimentRemoveBannersTracking(
    EXPERIMENTS_TRACK_EVENT_REMOVE_BANNERS_PDP_APP_INSTALL,
  )

  const experimentRemoveBannersAppsFlyerTracking = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>): void => {
      if (!(event.target instanceof HTMLElement)) {
        return
      }

      const hasCloseButtonQaId = event.target.dataset.qaId === 'close-button'
      if (hasCloseButtonQaId) {
        return
      }

      experimentRemoveBannersTracking()
    },
    [experimentRemoveBannersTracking],
  )

  if (isEnabled && variant === EXPERIMENT_VARIATION.V1) {
    return null
  }

  const additionalParams = {
    af_sub1: isEnabled ? variant : undefined,
  }

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- not for user interaction */}
      <div
        className={clsx(
          styles['apps-flyer-banner'],
          variant !== EXPERIMENT_VARIATION.V2 &&
            styles['apps-flyer-banner_sticky'],
        )}
        id={APPS_FLYER_CONTAINER_ID}
        onClick={experimentRemoveBannersAppsFlyerTracking}
      />
      {data ? (
        <Script id="apps-flyer">
          {/*
           * https://jira.shop-apotheke.com/browse/CRO-671
           * https://dev.appsflyer.com/hc/docs/dl_smart_banner_v2#smart-banners-only
           */}
          {`
!function(t,e,n,s,a,c,i,o,p){t.AppsFlyerSdkObject=a,t.AF=t.AF||function(){
(t.AF.q=t.AF.q||[]).push([Date.now()].concat(Array.prototype.slice.call(arguments)))},
t.AF.id=t.AF.id||i,t.AF.plugins={},o=e.createElement(n),p=e.getElementsByTagName(n)[0],o.async=1,
o.src="https://websdk.appsflyersdk.com?"+(c.length>0?"st="+c.split(",").sort().join(",")+"&":"")+(i.length>0?"af_id="+i:""),
p.parentNode.insertBefore(o,p)}(window,document,"script",0,"AF","banners",{banners: {key: "${key}"}});

AF('banners', 'showBanner', {
  additionalParams: ${JSON.stringify(additionalParams)},
  bannerContainerQuery: '#${APPS_FLYER_CONTAINER_ID}',
  bannerZIndex: 'auto',
});
dataLayer.push({ event: 'af_banner' });
        `}
        </Script>
      ) : null}
    </>
  )
}
